import * as Icon from 'base/ui/components/Icons';

import { Form, Modal, Tooltip, message } from 'antd';
import { SearchBox, SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionType } from '@ant-design/pro-components';

import { ExclamationCircleFilled } from '@ant-design/icons';
import { ProColumns } from '@ant-design/pro-components';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomButton from 'base/ui/components/Button';
import CustomBreadcrumb from 'base/ui/components/CustomBreadcrumb';
import CustomInput from 'base/ui/components/CustomInput';
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { usePermission } from 'hooks/usePermission';
import { observer } from 'mobx-react';
import { HiSearch } from 'react-icons/hi';
import { dropdownStatus } from 'utils/dropdown';
import { DataCatalog } from 'services/RawData/catalog_services';
import { WasteGroupingContractModel, WasteGroupingContractSearchPayload } from 'services/RawData/wasteGroup_services';
import { catalogPresenter, catalogStore, userStore, wasteGroupPresenter, wasteGroupStore } from 'stores/root_store';
import { DATE_HOUR_MINUTE_FORMAT_DEFAULT, PAGE_SIZE_DEFAULT } from 'utils/constants';
import { useTranslation } from 'react-i18next';
import { getRoles } from 'utils/localStorageHelper';

const { confirm } = Modal;

// phân nhóm chất thải (HĐ)
const createWasteGroupingContractPage = () => {
  return observer(() => {
    const { t } = useTranslation();
    usePermission('INDEX');
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const pagetitle = t('masterdata.waste-grouping-contract.title');

    // Check role SYSADMIN từ localStorage
    const roles = getRoles() || [];
    const rolesArray = Array.isArray(roles) ? roles : (typeof roles === 'string' ? roles.split(',').map(r => r.trim()) : []);
    const isSysAdmin = rolesArray.includes('SYSADMIN');

    const tableRef = useRef<ActionType>();
    const [openDrawer, setOpenDrawer] = useState(false);
    const [loading, setLoading] = useState(false);
    const [listData, setListData] = useState<WasteGroupingContractModel[]>([]);
    const [listWasteType, setListWasteType] = useState<DataCatalog[]>([]);
    const [dataSearch, setDataSearch] = useState<WasteGroupingContractSearchPayload>({
      paging: {
        pageIndex: 1,
        pageSize: PAGE_SIZE_DEFAULT,
        orderBy: '',
        orderByDesc: '',
      },
    });
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [importLoading, setImportLoading] = useState(false);

    // Lấy danh sách loại chất thải (WasteType) từ catalog
    const getListWasteType = async () => {
      await catalogPresenter.searchDropdownCatalog(catalogStore, 'WasteType');
      if (catalogStore.searchDropdownCatalogResponse.isSuccess) {
        setListWasteType(catalogStore.searchDropdownCatalogResponse.data);
      }
    };

    const fetchData = async (payload: WasteGroupingContractSearchPayload) => {
      await wasteGroupPresenter.searchWasteGroups(wasteGroupStore, payload);
      if (wasteGroupStore.searchResponse.isSuccess) {
        setListData(wasteGroupStore.searchResponse.data);
        return wasteGroupStore.searchResponse.data;
      }
      return [];
    };

    const columns: ProColumns<WasteGroupingContractModel>[] = [
      {
        title: t('masterdata.waste-grouping-contract.stt'),
        dataIndex: 'stt',
        width: 30,
        align: 'center',
        fixed: 'left',
      },
      {
        title: t('masterdata.waste-grouping-contract.sub-group-name'),
        dataIndex: 'wasteGroupingContractName',
        width: 200,
      },
      {
        title: t('masterdata.waste-grouping-contract.create-by'),
        dataIndex: 'userName',
        width: 120,
      },
      {
        title: t('masterdata.waste-grouping-contract.create-time'),
        dataIndex: 'createdTime',
        width: 100,
        align: 'center',
        valueType: 'dateTime',
        fieldProps: {
          format: DATE_HOUR_MINUTE_FORMAT_DEFAULT,
        },
      },
      {
        title: t('masterdata.waste-grouping-contract.status'),
        dataIndex: 'actived',
        width: 60,
        align: 'center',
        render: (_, record) => (
          <div className="w-full flex justify-center">
            {record.actived ? (
              <Icon.CheckStatus className="!fill-[#389E0D]" width="15" />
            ) : (
              <Icon.CloseStatus className="!fill-[#D93133E5]" width="15" />
            )}
          </div>
        ),
      },
      {
        title: t('masterdata.waste-grouping-contract.actions'),
        dataIndex: 'function',
        width: 60,
        align: 'center',
        fixed: 'right',
        render: (_, record) => (
          <div className="flex items-center justify-center gap-1">
            {userStore.userPermission?.includes('EDIT') ? (
              <Tooltip title={t('btn.edit')}>
                <CustomButton
                  className="!w-6 !h-6 rounded-sm !border-[#4DA8FF] flex items-center justify-center !bg-[#C9E8FF] hover:bg-[#95D2FF]"
                  icon={<Icon.PencilEdit className="!fill-[#007BF1]" width="16" height="16" />}
                  onClick={() => navigate(`${record.wasteGroupingContractId}`)}
                />
              </Tooltip>
            ) : null}
            {userStore.userPermission?.includes('DELETE') ? (
              <Tooltip title={t('btn.delete')}>
                <CustomButton
                  className="!w-6 !h-6 rounded-sm !border-[#FF4F51] flex items-center justify-center !bg-[#FFE5E6] hover:bg-[#FF8081]"
                  icon={<Icon.Trash className="!fill-[#FC5C5E]" width="16" height="16" />}
                  onClick={() => showDeleteConfirm(record.wasteGroupingContractId, record.wasteGroupingContractName)}
                />
              </Tooltip>
            ) : null}
          </div>
        ),
      },
    ];

    const showDeleteConfirm = (id: string, name: string) => {
      confirm({
        title: t('masterdata.waste-grouping-contract.delete-title'),
        icon: <ExclamationCircleFilled />,
        content: t('masterdata.waste-grouping-contract.delete-content', { name }),
        okText: t('btn.delete'),
        okType: 'danger',
        cancelText: t('btn.cancel'),
        async onOk() {
          setLoading(true);
          await wasteGroupPresenter.deleteWasteGroup(wasteGroupStore, id);
          if (wasteGroupStore.crudResponse.isSuccess) {
            message.success(wasteGroupStore.crudResponse.message);
            tableRef.current?.reload();
          } else {
            message.error(wasteGroupStore.crudResponse.message);
          }
          setLoading(false);
        },
      });
    };

    const handleSearch = async (values: any) => {
      const payload = {
        ...values,
        paging: {
          ...dataSearch.paging,
          pageIndex: 1,
        },
      };
      setDataSearch(payload);
      tableRef.current?.reload();
      setOpenDrawer(false);
    };

    useEffect(() => {
      getListWasteType();
    }, []);

    return (
      <div className="w-full">
        <CustomBreadcrumb
          pagetitle={[{ title: pagetitle, link: '/MasterData/WasteGroupingContract' }]}
          extra={
            <>
              {/* Import */}
              {isSysAdmin && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImportLoading(true);
                      try {
                        await wasteGroupPresenter.importWasteGroups(file);
                        tableRef.current?.reload();
                      } finally {
                        setImportLoading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  <CustomButton
                    onClick={() => fileInputRef.current?.click()}
                    loading={importLoading}
                    disabled={importLoading}
                  >
                    {t('btn.import') || 'Import'}
                  </CustomButton>
                </>
              )}

              {/* Export */}
              {isSysAdmin && (
                <CustomButton
                  onClick={async () => {
                    // dùng điều kiện search hiện tại để export
                    await wasteGroupPresenter.exportWasteGroups(dataSearch);
                  }}
                >
                  {t('btn.export') || 'Export'}
                </CustomButton>
              )}

              <CustomButton onClick={() => setOpenDrawer(true)} type="primary" color="#EFF4F9" className="!px-0 w-8">
                <Icon.Filter className="fill-primary" />
              </CustomButton>
              <CustomButton
                type="primary"
                icon={<Icon.Plus className="!fill-[#fff]" />}
                onClick={() => navigate('Create')}
              >
                {t('btn.add')}
              </CustomButton>
            </>
          }
        />
        <BoxContainer>
          <CustomProTable<WasteGroupingContractModel>
            columns={columns}
            rowKey="wasteGroupingContractId"
            bordered
            search={false}
            loading={loading}
            actionRef={tableRef}
            scroll={{ x: 1000 }}
            request={async (params = {}, sort, filter) => {
              setLoading(true);
              const values = form.getFieldsValue();
              const payload = {
                ...values,
                paging: {
                  ...dataSearch.paging,
                  orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                  orderByDesc: Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                },
              };
              const data: any = await fetchData(payload);
              if (Object.values(sort).length) {
                setDataSearch({
                  ...dataSearch,
                  paging: {
                    ...dataSearch.paging,
                    orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                    orderByDesc: Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                  },
                });
              }
              setLoading(false);
              return {
                data: data,
                success: wasteGroupStore.searchResponse.isSuccess,
              };
            }}
            pagination={{
              ...tablePagination(dataSearch, setDataSearch),
              total: wasteGroupStore.searchResponse.paging?.recordsTotal,
            }}
            options={false}
          />
        </BoxContainer>
        <SearchBox
          className="lg:!w-[35%]"
          footer={[
            <div key="footer" className="flex justify-end">
              <CustomButton
                type="primary"
                icon={<HiSearch />}
                htmlType="submit"
                onClick={() => {
                  form.submit();
                }}
              >
                {t('btn.search')}
              </CustomButton>
            </div>,
          ]}
          onClose={() => setOpenDrawer(false)}
          open={openDrawer}
        >
          <SearchForm column="col-1" form={form} onFinish={handleSearch}>
            <SearchItem label={t('masterdata.waste-grouping-contract.sub-group-name')} name="wasteGroupingContractName">
              <CustomInput placeholder={t('masterdata.waste-grouping-contract.sub-group-placeholder')} />
            </SearchItem>
            <SearchItem label={t('masterdata.waste-grouping-contract.status')} name="actived">
              <CustomSelect options={dropdownStatus} placeholder={t('place-holder.all')} allowClear />
            </SearchItem>
          </SearchForm>
        </SearchBox>
      </div>
    );
  });
};

export default createWasteGroupingContractPage;

